import { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { fmt, fmtDate, getVenue } from '../lib/utils';
import { Plus, Search, X, Mail, Phone, Edit2, Trash2, Download, Megaphone, Eye, MousePointerClick } from 'lucide-react';
import { fetchClientCampaigns } from '../lib/campaignService';
import { useNotify } from '../components/NotifyProvider';

export default function Clients({ onSelectEvent, onNavigate }) {
  const { clients, addClient, updateClient, deleteClient, events, payments, venues } = useAppContext();
  const { notifyError } = useNotify();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    tag: 'corporate',
    address: '',
    notes: '',
  });

  const tagColors = {
    corporate: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
    wedding: { bg: 'bg-pink-100', text: 'text-pink-800', dot: 'bg-pink-500' },
    private: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
    community: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  };

  // Filter clients
  const filteredClients = useMemo(() => {
    let result = clients;

    // Apply tag filter
    if (selectedFilter !== 'all') {
      result = result.filter((c) => c.tag === selectedFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          (c.company && c.company.toLowerCase().includes(term)) ||
          (c.email && c.email.toLowerCase().includes(term)) ||
          (c.phone && c.phone.toLowerCase().includes(term))
      );
    }

    return result;
  }, [clients, selectedFilter, searchTerm]);

  // Get client details including event history and revenue
  const getClientDetails = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    const clientEvents = client ? events.filter((e) => (e.clientId && e.clientId === client.id) || (!e.clientId && e.clientEmail === client.email)) : [];
    const clientPayments = payments.filter((p) =>
      clientEvents.some((e) => e.id === p.event_id)
    );

    const totalPackageRevenue = clientEvents.reduce((sum, e) => sum + (e.packageTotal || 0) + (e.roomHire || 0), 0);
    const totalConsumption = clientEvents.reduce((sum, e) => sum + (e.consumption || 0), 0);
    const totalPaymentAmount = clientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

    return {
      events: clientEvents,
      totalPackageRevenue,
      totalConsumption,
      totalPayments: totalPaymentAmount,
    };
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await addClient({
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        tag: formData.tag,
        address: formData.address,
        notes: formData.notes,
        totalEvents: 0,
        totalRevenue: 0,
        lastEvent: null,
      });
    } catch (err) {
      notifyError('Failed to save client: ' + (err?.message || 'unknown error'));
      return;
    }
    setShowAddForm(false);
    setFormData({
      name: '',
      company: '',
      phone: '',
      email: '',
      tag: 'corporate',
      address: '',
      notes: '',
    });
  };

  const handleOpenEditForm = (client) => {
    setEditingClientId(client.id);
    setFormData({
      name: client.name,
      company: client.company || '',
      phone: client.phone || '',
      email: client.email || '',
      tag: client.tag || 'corporate',
      address: client.address || '',
      notes: client.notes || '',
    });
    setShowEditForm(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      await updateClient(editingClientId, {
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        tag: formData.tag,
        address: formData.address,
        notes: formData.notes,
      });
    } catch (err) {
      notifyError('Failed to save changes: ' + (err?.message || 'unknown error'));
      return;
    }
    setShowEditForm(false);
    setEditingClientId(null);
    setFormData({
      name: '',
      company: '',
      phone: '',
      email: '',
      tag: 'corporate',
      address: '',
      notes: '',
    });
  };

  const handleDeleteClient = (client) => {
    setClientToDelete(client);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    const target = clientToDelete;
    setClientToDelete(null);
    try {
      await deleteClient(target.id);
    } catch (err) {
      notifyError('Failed to delete client: ' + (err?.message || 'unknown error') + ' — it has been restored.');
    }
  };

  const handleExportCSV = () => {
    // Create CSV header
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Total Events', 'Total Revenue'];

    // Create CSV rows
    const rows = clients.map((client) => {
      const details = getClientDetails(client.id);
      const totalRevenue = details.totalPackageRevenue + details.totalConsumption;
      return [
        `"${client.name}"`,
        `"${client.email || ''}"`,
        `"${client.phone || ''}"`,
        `"${client.company || ''}"`,
        details.events.length,
        totalRevenue,
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    // Create a blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `errigal-clients-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Client Database</h1>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleExportCSV}
            disabled={clients.length === 0}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 h-10 rounded-lg flex items-center gap-2 text-sm font-medium w-fit"
          >
            <Download size={16} /> Export for Email Marketing
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 h-10 rounded-lg flex items-center gap-2 text-sm font-medium w-fit"
          >
            <Plus size={16} /> Add Client
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, company, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'corporate', 'wedding', 'private', 'community'].map((filter) => (
          <button
            key={filter}
            onClick={() => setSelectedFilter(filter)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              selectedFilter === filter
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      {/* Client List */}
      <div className="space-y-4">
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-4 md:p-8 text-center">
            <p className="text-gray-500">No clients found</p>
          </div>
        ) : (
          filteredClients.map((client) => {
            const isExpanded = expandedClientId === client.id;
            const details = getClientDetails(client.id);
            const tagStyle = tagColors[client.tag] || tagColors.corporate;

            return (
              <div key={client.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Client Card Header */}
                <button
                  onClick={() =>
                    setExpandedClientId(isExpanded ? null : client.id)
                  }
                  className="w-full text-left p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${tagStyle.bg} ${tagStyle.text}`}>
                          {client.tag}
                        </span>
                      </div>
                      {client.company && (
                        <p className="text-sm text-gray-600 mb-2">{client.company}</p>
                      )}
                      <div className="flex flex-wrap gap-3 text-sm">
                        <a
                          href={`tel:${client.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <Phone size={14} /> {client.phone}
                        </a>
                        <a
                          href={`mailto:${client.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <Mail size={14} /> {client.email}
                        </a>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {fmt(details.totalPackageRevenue + details.totalConsumption)}
                      </div>
                      <p className="text-xs text-gray-500">
                        {details.events.length} event{details.events.length !== 1 ? 's' : ''}
                      </p>
                      {details.events.length > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Last: {fmtDate(details.events[details.events.length - 1].date)}
                        </p>
                      )}
                    </div>
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50 space-y-4">
                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => handleOpenEditForm(client)}
                        className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 h-10 rounded text-sm font-medium flex items-center gap-2"
                      >
                        <Edit2 size={14} /> Edit Client
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client)}
                        className="bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 h-10 rounded text-sm font-medium flex items-center gap-2"
                      >
                        <Trash2 size={14} /> Delete Client
                      </button>
                    </div>

                    {/* Full Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Name</p>
                        <p className="text-sm text-gray-900">{client.name}</p>
                      </div>
                      {client.company && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Company</p>
                          <p className="text-sm text-gray-900">{client.company}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Phone</p>
                        <a
                          href={`tel:${client.phone}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {client.phone}
                        </a>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                        <a
                          href={`mailto:${client.email}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {client.email}
                        </a>
                      </div>
                      {client.address && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Address</p>
                          <p className="text-sm text-gray-900">{client.address}</p>
                        </div>
                      )}
                      {client.notes && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Notes</p>
                          <p className="text-sm text-gray-900">{client.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Revenue Summary */}
                    <div className="bg-white rounded p-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Revenue Summary</h4>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-gray-500">Package</p>
                          <p className="font-semibold text-gray-900">
                            {fmt(details.totalPackageRevenue)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Consumption</p>
                          <p className="font-semibold text-gray-900">
                            {fmt(details.totalConsumption)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Paid</p>
                          <p className="font-semibold text-green-600">
                            {fmt(details.totalPayments)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Booking History */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Booking History</h4>
                      {details.events.length === 0 ? (
                        <p className="text-xs text-gray-500">No events recorded</p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {details.events.map((event) => (
                            <div
                              key={event.id}
                              onClick={() => onSelectEvent?.(event)}
                              className="text-xs bg-white rounded p-2 border border-gray-100 cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold text-blue-700 hover:text-blue-900">{event.name}</p>
                                  <p className="text-gray-600">
                                    {fmtDate(event.date)} at {getVenue(event.venue_id, venues)?.name || 'TBC'}
                                  </p>
                                </div>
                                <span className="text-right">
                                  <p className="font-semibold text-gray-900">{fmt(event.packageTotal)}</p>
                                  <p className="text-gray-500 capitalize">{event.stage}</p>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Marketing Email History */}
                    <ClientCampaignHistory clientId={client.id} />

                    {/* Add Event Button */}
                    <button
                      onClick={() => onNavigate?.('events')}
                      className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 h-10 rounded text-sm font-medium"
                    >
                      + Add Event for This Client
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Client Modal */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                {showAddForm ? 'Add New Client' : 'Edit Client'}
              </h2>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setShowEditForm(false);
                  setEditingClientId(null);
                  setFormData({
                    name: '',
                    company: '',
                    phone: '',
                    email: '',
                    tag: 'corporate',
                    address: '',
                    notes: '',
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={showAddForm ? handleAddClient : handleUpdateClient} className="p-4 md:p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Type</label>
                <select
                  name="tag"
                  value={formData.tag}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="corporate">Corporate</option>
                  <option value="wedding">Wedding</option>
                  <option value="private">Private</option>
                  <option value="community">Community</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 h-10 rounded font-medium text-sm"
              >
                {showAddForm ? 'Add Client' : 'Update Client'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {clientToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-4 md:p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Confirm Delete</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete <strong>{clientToDelete.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setClientToDelete(null)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 h-10 rounded font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteClient}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 h-10 rounded font-medium text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Client Campaign History Component ───
function ClientCampaignHistory({ clientId }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    fetchClientCampaigns(clientId)
      .then((data) => {
        setCampaigns(data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) {
    return (
      <div className="text-xs text-gray-400 py-2">Loading marketing history...</div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
        <Megaphone size={14} className="text-emerald-600" />
        Marketing Emails
      </h4>
      {!loaded || campaigns.length === 0 ? (
        <p className="text-xs text-gray-500">No marketing emails sent to this client</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {campaigns.map((r) => (
            <div key={r.id} className="text-xs bg-white rounded p-2 border border-gray-100">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-700">
                    {r.email_campaigns?.name || 'Campaign'}
                  </p>
                  <p className="text-gray-500">{r.email_campaigns?.subject}</p>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  {r.opened_at && (
                    <span className="flex items-center gap-0.5 text-purple-600" title="Opened">
                      <Eye size={11} /> {r.open_count || 1}
                    </span>
                  )}
                  {r.clicked_at && (
                    <span className="flex items-center gap-0.5 text-indigo-600" title="Clicked">
                      <MousePointerClick size={11} /> {r.click_count || 1}
                    </span>
                  )}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    r.status === 'opened' || r.status === 'clicked'
                      ? 'bg-emerald-100 text-emerald-700'
                      : r.status === 'bounced'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {r.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}